import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Brain } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/App";
import { logger } from "@/utils/logger";
import { ElementsGrid, WhatToExpect, ParticipantForm } from "@/components/HomeSections";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_NAME = "Platyp@dmin";

const validateParticipantForm = (name, gender, age, experience) => {
  if (!name) return "Please enter your name";
  if (!gender) return "Please select your gender";
  if (!age) return "Please select your age range";
  if (!experience) return "Please select your years of experience";
  return null;
};

export default function HomePage() {
  const { session, startSession } = useSession();
  const [userName, setUserName] = useState("");
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [experience, setExperience] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (session && session.user_type === "admin") navigate("/admin");
  }, [session, navigate]);

  const handleAdminEntry = () => {
    startSession({ user_type: "admin", user_name: ADMIN_NAME });
    toast.success("Welcome, Admin!");
    navigate("/admin");
  };

  const handleStartAssessment = async () => {
    const trimmedName = userName.trim();
    if (!trimmedName) {
      toast.error("Please enter your name");
      return;
    }
    if (trimmedName.toLowerCase() === ADMIN_NAME.toLowerCase()) {
      handleAdminEntry();
      return;
    }

    const error = validateParticipantForm(trimmedName, gender, ageRange, experience);
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/assessment/start`, {
        user_name: trimmedName,
        gender,
        age_range: ageRange,
        years_experience: experience
      });
      const assessmentId = response.data.id;
      startSession({
        user_type: "participant",
        user_name: trimmedName,
        gender,
        age_range: ageRange,
        years_experience: experience,
        current_assessment_id: assessmentId,
        assessment_state: "assessment",
        last_scenario: 1
      });
      navigate(`/assessment/${assessmentId}`);
    } catch (err) {
      logger.error("Error starting assessment:", err);
      toast.error("Failed to start assessment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full space-y-8 fade-in">
        <div className="text-center space-y-4">
          <div className="flex justify-center mb-6">
            <div className="bg-gradient-to-br from-[#FF751F] to-[#FFBD25] p-4 rounded-2xl shadow-lg">
              <Brain className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight px-2" style={{ color: '#231F20' }}>
            Brain 360
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl max-w-2xl mx-auto font-semibold px-4" style={{ color: '#231F20' }}>
            Find your elemental archetype
          </p>
          <p className="text-base sm:text-lg md:text-xl max-w-2xl mx-auto mt-2 px-4" style={{ color: '#4b5563' }}>
            A psychometric assessment designed to evaluate your leadership style
          </p>
        </div>

        <Card className="shadow-2xl border-0 bg-white">
          <CardContent className="space-y-8 pt-8">
            <ElementsGrid />
            <WhatToExpect />
            <ParticipantForm
              userName={userName} setUserName={setUserName}
              gender={gender} setGender={setGender}
              ageRange={ageRange} setAgeRange={setAgeRange}
              experience={experience} setExperience={setExperience}
              loading={loading} onSubmit={handleStartAssessment}
            />
          </CardContent>
        </Card>

        <div className="text-center space-y-4">
          <p className="text-sm" style={{ color: '#4b5563' }}>
            Your responses are confidential and used only for generating your personalized report.
          </p>
        </div>
      </div>
    </div>
  );
}
